/**
 * TikTok Pixel Analytics Plugin
 * 
 * Isolated plugin responsible solely for TikTok Pixel browser events.
 * Fully decoupled from Facebook, GTM, and application UI components.
 */

import { AnalyticsPlugin, AnalyticsEventPayload } from '../types';

export class TikTokPlugin implements AnalyticsPlugin {
  readonly name = 'tiktok';
  private maxRetries = 3;

  isReady(): boolean {
    return typeof window !== 'undefined' && typeof window.ttq === 'object' && typeof window.ttq.track === 'function';
  }

  async track(eventName: string, payload: AnalyticsEventPayload): Promise<void> {
    if (typeof window === 'undefined') return;

    // 1. Prepare TikTok-compliant payload
    const ttData: Record<string, any> = { ...payload };

    // Advanced Matching: extract & normalize phone and email
    const rawPhone = payload.phone || payload.phone_number || payload.customer_phone || '';
    let rawEmail = payload.email || payload.customer_email || '';

    // Check logged-in user fallback for email if not passed directly
    if (!rawEmail) {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser?.email) {
          rawEmail = storedUser.email;
        }
      } catch {}
    }

    let normalizedPhone = '';
    if (rawPhone) {
      const digits = String(rawPhone).replace(/\D/g, '');
      if (digits.startsWith('880')) {
        normalizedPhone = `+${digits}`;
      } else if (digits.startsWith('0')) {
        normalizedPhone = `+88${digits}`;
      } else if (digits.length >= 8) {
        normalizedPhone = `+${digits}`;
      }
    }

    const normalizedEmail = rawEmail ? String(rawEmail).trim().toLowerCase() : '';

    if (normalizedPhone) {
      ttData.phone_number = normalizedPhone;
    }
    if (normalizedEmail) {
      ttData.email = normalizedEmail;
    }

    // Determine primary content_id based on catalog ID format (id vs sku)
    const idFormat = localStorage.getItem('tt_catalog_id_format') || 'id';
    let primaryId = payload.content_id;
    if (idFormat === 'sku' && payload.sku) {
      primaryId = payload.sku;
    }
    if (!primaryId && Array.isArray(payload.content_ids) && payload.content_ids.length > 0) {
      primaryId = payload.content_ids[0];
    }
    if (!primaryId && Array.isArray(payload.contents) && payload.contents.length > 0) {
      primaryId = payload.contents[0].content_id;
    }

    if (primaryId) {
      ttData.content_id = String(primaryId);
      ttData.content_type = payload.content_type || 'product';
    }

    // Standardize contents array
    if (Array.isArray(payload.content_ids) && payload.content_ids.length > 0 && !payload.contents) {
      const quantity = payload.num_items || payload.quantity || 1;
      const contentType = payload.content_type || 'product';
      const contentName = payload.content_name || '';
      const itemPrice = payload.value ? (payload.value / quantity) : (payload.price || 0);

      ttData.contents = payload.content_ids.map((id) => ({
        content_id: String(id),
        content_type: contentType,
        content_name: contentName,
        price: itemPrice,
        quantity: quantity,
      }));
    }

    if (payload.search_string) {
      ttData.query = payload.search_string;
    }

    const eventId = payload.event_id || `${eventName}_${Date.now()}`;
    const options = { event_id: eventId };

    // Standardize event name: modern TikTok standard conversion event is 'Purchase'
    const ttEventName = eventName;

    let attempts = 0;
    const execute = (): Promise<void> => {
      return new Promise((resolve) => {
        const attemptTrack = () => {
          if (this.isReady()) {
            // Identify user for Advanced Matching if phone or email is available
            if (typeof window.ttq.identify === 'function' && (normalizedPhone || normalizedEmail)) {
              try {
                const identifyPayload: Record<string, string> = {};
                if (normalizedPhone) identifyPayload.phone_number = normalizedPhone;
                if (normalizedEmail) identifyPayload.email = normalizedEmail;
                window.ttq.identify(identifyPayload);
              } catch (e) {
                // Non-blocking
              }
            }

            window.ttq.track(ttEventName, ttData, options);

            // Legacy campaign fallback for purchases
            if (eventName === 'Purchase') {
              window.ttq.track('CompletePayment', ttData, { event_id: `${eventId}_cp` });
            }
            resolve();
          } else if (attempts < this.maxRetries) {
            attempts++;
            setTimeout(attemptTrack, 100);
          } else {
            resolve();
          }
        };
        attemptTrack();
      });
    };

    await execute();
  }
}
